<template>
  <AppLayout>
    <div class="usine-page">
      <div class="page-header">
        <h1>Usine</h1>
      </div>

      <BaseCard class="filters-card">
        <div class="filters-grid">
          <BaseSelect v-model="selectedProjetId" label="Projet">
            <option value="">Tous les projets</option>
            <option v-for="projet in projets" :key="projet.id" :value="String(projet.id)">
              {{ projet.nom }} ({{ projet.code }})
            </option>
          </BaseSelect>

          <BaseSelect v-model="selectedSiteId" label="Site" :disabled="!selectedProjetId">
            <option value="">{{ selectedProjetId ? 'Tous les sites' : '-- Choisir un projet --' }}</option>
            <option v-for="site in filteredSites" :key="site.id" :value="String(site.id)">
              {{ site.nom }}
            </option>
          </BaseSelect>

          <BaseInput
            v-model="horizonDate"
            label="Livrable à la date"
            type="date"
          />

        </div>
      </BaseCard>

      <div class="page-tabs">
        <button class="page-tab" :class="{ active: activeTab === 'flux' }" @click="activeTab = 'flux'">Flux usine</button>
        <button class="page-tab" :class="{ active: activeTab === 'receptions' }" @click="activeTab = 'receptions'">Réceptions matériaux</button>
        <button class="page-tab" :class="{ active: activeTab === 'production' }" @click="activeTab = 'production'">Historique production</button>
        <button class="page-tab" :class="{ active: activeTab === 'livraisons' }" @click="activeTab = 'livraisons'">Livraisons</button>
        <button class="page-tab" :class="{ active: activeTab === 'conso' }" @click="activeTab = 'conso'">Consommation matériaux</button>
        <div class="tab-actions">
          <BaseButton v-if="activeTab === 'receptions'" variant="primary" size="sm" @click="openLivraisonMateriauModal">
            + Réception matériau
          </BaseButton>
          <BaseButton v-if="activeTab === 'conso'" variant="primary" size="sm" @click="openConsoModal">
            + Consommation matériau
          </BaseButton>
          <BaseButton v-if="activeTab === 'livraisons'" variant="primary" size="sm" @click="openExpeditionModal">
            + Nouvelle livraison
          </BaseButton>
          <BaseButton v-if="activeTab === 'production'" variant="primary" size="sm" @click="openProductionModal">
            + Production du jour
          </BaseButton>
        </div>
      </div>

      <div v-if="error" class="error">{{ error }}</div>

      <section v-show="activeTab === 'flux'" class="panel-section">
        <div class="section-header">
          <h2>Flux usine</h2>
        </div>
        <BaseTable
          :columns="fluxColumns"
          :items="fluxRowsFiltered"
          :loading="loadingFlux"
          :show-actions="false"
        />
      </section>

      <section v-show="activeTab === 'receptions'" class="panel-section">
        <div class="section-header">
          <h2>Réceptions matériaux</h2>
        </div>
        <BaseTable
          :columns="livraisonMateriauColumns"
          :items="receptionsPaged"
          :loading="loadingLivraisonsMateriaux"
          :show-actions="false"
        >
          <template #cell-dateLivraison="{ value }">
            {{ value ? formatDate(value) : '-' }}
          </template>
          <template #cell-typeMateriau="{ item }">
            {{ resolveTypeMateriauName(item.typeMateriauId) }}
          </template>
          <template #cell-unite="{ item }">
            {{ resolveTypeMateriauUnite(item.typeMateriauId) }}
          </template>
          <template #cell-projet="{ item }">
            {{ resolveProjetLabel(item.projetId) }}
          </template>
        </BaseTable>
        <div v-if="receptionsTotalPages > 1" class="pagination-bar">
          <button class="pag-btn" :disabled="pageReceptions <= 1" @click="pageReceptions--">‹</button>
          <span class="pag-info">{{ pageReceptions }} / {{ receptionsTotalPages }}</span>
          <button class="pag-btn" :disabled="pageReceptions >= receptionsTotalPages" @click="pageReceptions++">›</button>
        </div>
      </section>

      <section v-show="activeTab === 'production'" class="panel-section">
        <div class="section-header">
          <h2>Historique production</h2>
        </div>
        <BaseTable
          :columns="lotColumns"
          :items="productionPaged"
          :loading="loadingLots"
          :show-actions="true"
          @view="viewLot"
        >
          <template #cell-dateProduction="{ value }">
            {{ value ? formatDate(value) : '-' }}
          </template>
          <template #cell-projet="{ item }">
            {{ item.projetNom }} ({{ item.projetCode }})
          </template>
          <template #cell-site="{ item }">
            {{ item.siteNom ?? '-' }}
          </template>
          <template #cell-typeElement="{ item }">
            {{ resolveTypeLabel(item.typeElementPrefabriqueId) }}
          </template>
          <template #cell-statutMaturation="{ value }">
            <BaseBadge v-if="value" :variant="value === 'DISPONIBLE' ? 'success' : 'warning'">{{ value }}</BaseBadge>
            <span v-else>-</span>
          </template>
          <template #actions="{ item }">
            <BaseButton variant="ghost" size="sm" @click.stop="openEditLotModal(item)">Modifier</BaseButton>
            <BaseButton
              v-if="item.statutMaturation === 'EN_MATURATION'"
              variant="primary" size="sm"
              :loading="forcingMaturationId === item.id"
              @click.stop="forcerFinMaturation(item.id)"
            >
              Fin maturation
            </BaseButton>
          </template>
        </BaseTable>
        <div v-if="productionTotalPages > 1" class="pagination-bar">
          <button class="pag-btn" :disabled="pageProduction <= 1" @click="pageProduction--">‹</button>
          <span class="pag-info">{{ pageProduction }} / {{ productionTotalPages }}</span>
          <button class="pag-btn" :disabled="pageProduction >= productionTotalPages" @click="pageProduction++">›</button>
        </div>
      </section>

      <section v-show="activeTab === 'livraisons'" class="panel-section">
        <div class="section-header">
          <h2>Livraisons</h2>
        </div>
        <BaseTable
          :columns="expeditionColumns"
          :items="livraisonsPaged"
          :loading="loadingExpeditions"
          :show-actions="true"
        >
          <template #cell-dateExpedition="{ value }">
            {{ value ? formatDate(value) : '-' }}
          </template>
          <template #cell-projet="{ item }">
            {{ resolveProjetLabel(item.projetId) }}
          </template>
          <template #cell-site="{ item }">
            {{ item.siteNom ?? '-' }}
          </template>
          <template #cell-statut="{ value }">
            <BaseBadge :variant="getExpeditionStatutVariant(value)">{{ value }}</BaseBadge>
          </template>
          <template #actions="{ item }">
            <BaseButton variant="ghost" size="sm" @click="viewExpedition(item)">Voir</BaseButton>
          </template>
        </BaseTable>
        <div v-if="livraisonsTotalPages > 1" class="pagination-bar">
          <button class="pag-btn" :disabled="pageLivraisons <= 1" @click="pageLivraisons--">‹</button>
          <span class="pag-info">{{ pageLivraisons }} / {{ livraisonsTotalPages }}</span>
          <button class="pag-btn" :disabled="pageLivraisons >= livraisonsTotalPages" @click="pageLivraisons++">›</button>
        </div>
      </section>

      <section v-show="activeTab === 'conso'" class="panel-section">
        <div class="section-header">
          <h2>Consommation matériaux</h2>
        </div>
        <BaseTable
          :columns="consoColumns"
          :items="consoPaged"
          :loading="loadingConso"
          :show-actions="true"
        >
          <template #cell-dateConso="{ value }">
            {{ value ? formatDate(String(value)) : '-' }}
          </template>
          <template #actions="{ item }">
            <BaseButton variant="ghost" size="sm" @click.stop="deleteConso(Number((item as any).id))">Supprimer</BaseButton>
          </template>
        </BaseTable>
        <div v-if="consoTotalPages > 1" class="pagination-bar">
          <button class="pag-btn" :disabled="pageConso <= 1" @click="pageConso--">‹</button>
          <span class="pag-info">{{ pageConso }} / {{ consoTotalPages }}</span>
          <button class="pag-btn" :disabled="pageConso >= consoTotalPages" @click="pageConso++">›</button>
        </div>
      </section>
    </div>

    <FormPage v-if="showConsoModal" :wide="true" @close="closeConsoModal">
      <template #title>Nouvelle consommation matériau</template>
      <form class="modal-form" @submit.prevent="saveConso">

        <!-- Desktop : tableau -->
        <div class="production-rows-table-wrap">
          <table class="production-rows-table">
            <thead>
              <tr>
                <th>Date *</th>
                <th>Projet *</th>
                <th>N° OP</th>
                <th>N° DA</th>
                <th>Unité prod.</th>
                <th>Désignation *</th>
                <th>Magasin</th>
                <th>Matériaux / Matériels *</th>
                <th>Quantité *</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(row, index) in consoRows" :key="index">
                <td><input v-model="row.dateConso" type="date" required class="row-input" style="width:130px" /></td>
                <td><input v-model.trim="row.projet" type="text" placeholder="Projet" required class="row-input" style="width:130px" /></td>
                <td><input v-model.trim="row.numeroOp" type="text" placeholder="N° OP" class="row-input" style="width:90px" /></td>
                <td><input v-model.trim="row.numeroDa" type="text" placeholder="N° DA" class="row-input" style="width:90px" /></td>
                <td><input v-model.trim="row.uniteProd" type="text" placeholder="Unité" class="row-input" style="width:100px" /></td>
                <td><input v-model.trim="row.designation" type="text" placeholder="Désignation" required class="row-input" style="width:160px" /></td>
                <td><input v-model.trim="row.magasin" type="text" placeholder="Magasin" class="row-input" style="width:100px" /></td>
                <td><input v-model.trim="row.materiau" type="text" placeholder="Matériau" required class="row-input" style="width:140px" /></td>
                <td><input v-model="row.quantite" type="number" step="0.001" min="0" placeholder="0" required class="row-input row-input-sm" /></td>
                <td><button type="button" class="row-delete-btn" :disabled="consoRows.length === 1" @click="removeConsoRow(index)">×</button></td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Mobile : cartes empilées -->
        <div class="production-rows-cards">
          <div v-for="(row, index) in consoRows" :key="index" class="production-row-card">
            <div class="row-card-header">
              <span class="row-card-num">Ligne {{ index + 1 }}</span>
              <button type="button" class="row-delete-btn" :disabled="consoRows.length === 1" @click="removeConsoRow(index)">×</button>
            </div>
            <div class="row-card-fields">
              <label class="row-card-field"><span>Date *</span><input v-model="row.dateConso" type="date" required class="row-input" /></label>
              <label class="row-card-field"><span>Projet *</span><input v-model.trim="row.projet" type="text" placeholder="Nom du projet" required class="row-input" /></label>
              <label class="row-card-field"><span>N° OP</span><input v-model.trim="row.numeroOp" type="text" placeholder="Numéro OP" class="row-input" /></label>
              <label class="row-card-field"><span>N° DA</span><input v-model.trim="row.numeroDa" type="text" placeholder="Numéro DA" class="row-input" /></label>
              <label class="row-card-field"><span>Unité de production</span><input v-model.trim="row.uniteProd" type="text" placeholder="Ex: Bâtiment A" class="row-input" /></label>
              <label class="row-card-field"><span>Désignation *</span><input v-model.trim="row.designation" type="text" placeholder="Description" required class="row-input" /></label>
              <label class="row-card-field"><span>Magasin</span><input v-model.trim="row.magasin" type="text" placeholder="Lieu de stockage" class="row-input" /></label>
              <label class="row-card-field"><span>Matériaux / Matériels *</span><input v-model.trim="row.materiau" type="text" placeholder="Type de matériau" required class="row-input" /></label>
              <label class="row-card-field"><span>Quantité *</span><input v-model="row.quantite" type="number" step="0.001" min="0" placeholder="0" required class="row-input" /></label>
            </div>
          </div>
        </div>

        <div>
          <BaseButton type="button" variant="ghost" size="sm" @click="addConsoRow">+ Ajouter une ligne</BaseButton>
        </div>

        <div v-if="consoFormError" class="form-error">{{ consoFormError }}</div>
        <div class="form-actions">
          <BaseButton type="button" variant="ghost" @click="closeConsoModal">Annuler</BaseButton>
          <BaseButton type="submit" variant="primary" :loading="savingConso">Enregistrer</BaseButton>
        </div>
      </form>
    </FormPage>

    <FormPage v-if="showProductionModal" :wide="!editingLot" @close="closeProductionModal">
      <template #title>{{ editingLot ? 'Modifier lot de production' : 'Renseigner production du jour' }}</template>
      <form class="modal-form" @submit.prevent="saveProduction">

        <!-- MODE ÉDITION : formulaire simple inchangé -->
        <template v-if="editingLot">
          <BaseSelect v-model="productionForm.projetId" label="Projet *" required>
            <option value="">-- Sélectionner --</option>
            <option v-for="projet in projets" :key="projet.id" :value="String(projet.id)">
              {{ projet.nom }} ({{ projet.code }})
            </option>
          </BaseSelect>

          <BaseSelect v-model="productionForm.siteId" label="Site">
            <option value="">Aucun site</option>
            <option
              v-for="site in productionFormSites"
              :key="site.id"
              :value="String(site.id)"
            >
              {{ site.nom }}
            </option>
          </BaseSelect>

          <BaseSelect v-model="productionForm.typeElementPrefabriqueId" label="Type d'élément *" required>
            <option value="">-- Sélectionner --</option>
            <option v-for="type in typesElements" :key="type.id" :value="String(type.id)">
              {{ type.libelle }} ({{ type.code }})
            </option>
          </BaseSelect>

          <BaseInput
            v-model="productionForm.quantiteProduite"
            label="Quantité produite *"
            type="number"
            placeholder="0"
          />

          <div v-if="productionRestantInfo" class="besoin-info">
            <span>Commandé : <strong>{{ productionRestantInfo.commandee }}</strong></span>
            <span> — Déjà produit : <strong>{{ productionRestantInfo.produite }}</strong></span>
            <span> — Restant : <strong>{{ productionRestantInfo.restant }}</strong></span>
          </div>
          <div v-if="productionDepasseCommandee" class="form-warning">
            La quantité totale produite dépassera la quantité commandée.
          </div>

          <BaseInput
            v-model="productionForm.dureeMaturationJours"
            label="Maturation (jours) *"
            type="number"
            placeholder="0"
            hint="Nécessaire quand la quantité produite augmente."
          />

          <BaseInput
            v-model="productionForm.dateProduction"
            label="Date de production *"
            type="date"
          />
        </template>

        <!-- MODE CRÉATION : tableau multi-lignes -->
        <template v-else>
          <!-- Desktop : tableau -->
          <div class="production-rows-table-wrap">
            <table class="production-rows-table">
              <thead>
                <tr>
                  <th>Projet *</th>
                  <th>Site</th>
                  <th>Type d'élément *</th>
                  <th>Quantité *</th>
                  <th>Maturation (j)</th>
                  <th>Date *</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(row, index) in productionRows" :key="index">
                  <td>
                    <select v-model="row.projetId" @change="onRowProjetChange(index)" class="row-select" required>
                      <option value="">--</option>
                      <option v-for="p in projets" :key="p.id" :value="String(p.id)">{{ p.nom }} ({{ p.code }})</option>
                    </select>
                  </td>
                  <td>
                    <select v-model="row.siteId" class="row-select">
                      <option value="">Aucun</option>
                      <option v-for="s in getSitesForRow(row.projetId)" :key="s.id" :value="String(s.id)">{{ s.nom }}</option>
                    </select>
                  </td>
                  <td>
                    <select v-model="row.typeElementPrefabriqueId" class="row-select" required>
                      <option value="">--</option>
                      <option v-for="t in typesElements" :key="t.id" :value="String(t.id)">{{ t.libelle }} ({{ t.code }})</option>
                    </select>
                  </td>
                  <td>
                    <input v-model="row.quantiteProduite" type="number" min="0" placeholder="0" required class="row-input" />
                  </td>
                  <td>
                    <input v-model="row.dureeMaturationJours" type="number" min="0" placeholder="0" class="row-input row-input-sm" />
                  </td>
                  <td>
                    <input v-model="row.dateProduction" type="date" required class="row-input" />
                  </td>
                  <td>
                    <button type="button" class="row-delete-btn" :disabled="productionRows.length === 1" @click="removeProductionRow(index)">×</button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Mobile : cartes empilées -->
          <div class="production-rows-cards">
            <div v-for="(row, index) in productionRows" :key="index" class="production-row-card">
              <div class="row-card-header">
                <span class="row-card-num">Lot {{ index + 1 }}</span>
                <button type="button" class="row-delete-btn" :disabled="productionRows.length === 1" @click="removeProductionRow(index)">×</button>
              </div>
              <div class="row-card-fields">
                <label class="row-card-field">
                  <span>Projet *</span>
                  <select v-model="row.projetId" @change="onRowProjetChange(index)" class="row-select" required>
                    <option value="">-- Sélectionner --</option>
                    <option v-for="p in projets" :key="p.id" :value="String(p.id)">{{ p.nom }} ({{ p.code }})</option>
                  </select>
                </label>
                <label class="row-card-field">
                  <span>Site</span>
                  <select v-model="row.siteId" class="row-select">
                    <option value="">Aucun site</option>
                    <option v-for="s in getSitesForRow(row.projetId)" :key="s.id" :value="String(s.id)">{{ s.nom }}</option>
                  </select>
                </label>
                <label class="row-card-field">
                  <span>Type d'élément *</span>
                  <select v-model="row.typeElementPrefabriqueId" class="row-select" required>
                    <option value="">-- Sélectionner --</option>
                    <option v-for="t in typesElements" :key="t.id" :value="String(t.id)">{{ t.libelle }} ({{ t.code }})</option>
                  </select>
                </label>
                <label class="row-card-field">
                  <span>Quantité *</span>
                  <input v-model="row.quantiteProduite" type="number" min="0" placeholder="0" required class="row-input" />
                </label>
                <label class="row-card-field">
                  <span>Maturation (j)</span>
                  <input v-model="row.dureeMaturationJours" type="number" min="0" placeholder="0" class="row-input" />
                </label>
                <label class="row-card-field">
                  <span>Date *</span>
                  <input v-model="row.dateProduction" type="date" required class="row-input" />
                </label>
              </div>
            </div>
          </div>

          <div>
            <BaseButton type="button" variant="ghost" size="sm" @click="addProductionRow">+ Ajouter une ligne</BaseButton>
          </div>
        </template>

        <div v-if="productionFormError" class="form-error">{{ productionFormError }}</div>
        <div class="form-actions">
          <BaseButton type="button" variant="ghost" @click="closeProductionModal">Annuler</BaseButton>
          <BaseButton type="submit" variant="primary" :loading="savingProduction">
            {{ editingLot ? 'Mettre à jour' : 'Enregistrer' }}
          </BaseButton>
        </div>
      </form>
    </FormPage>

    <FormPage v-if="showExpeditionModal" :wide="true" @close="closeExpeditionModal">
      <template #title>Nouvelle livraison</template>
      <form class="modal-form" @submit.prevent="saveExpedition">
        <div class="expedition-form-grid">
          <BaseSelect v-model="expeditionForm.projetId" label="Projet *" required>
            <option value="">-- Sélectionner --</option>
            <option v-for="projet in projets" :key="projet.id" :value="String(projet.id)">
              {{ projet.nom }} ({{ projet.code }})
            </option>
          </BaseSelect>

          <BaseSelect v-model="expeditionForm.siteId" label="Site *" required :disabled="!expeditionForm.projetId">
            <option value="">{{ expeditionForm.projetId ? '-- Sélectionner --' : '-- Choisir un projet --' }}</option>
            <option v-for="site in expeditionFormSites" :key="site.id" :value="String(site.id)">
              {{ site.nom }}
            </option>
          </BaseSelect>

          <BaseInput v-model="expeditionForm.dateExpedition" label="Date expédition *" type="date" />
          <BaseInput v-model.trim="expeditionForm.idVehicule" label="Véhicule" placeholder="Immatriculation" />
          <BaseInput v-model.trim="expeditionForm.nomChauffeur" label="Chauffeur" placeholder="Nom du chauffeur" />
          <BaseInput v-model.number="expeditionForm.carburantLitres" label="Carburant (litres)" type="number" step="0.1" min="0" placeholder="0" />
          <BaseInput v-model.number="expeditionForm.distanceKm" label="Distance (km)" type="number" step="0.1" min="0" placeholder="0" />
        </div>

        <div v-if="expeditionSuggestions.length > 0" class="expedition-items-section">
          <div class="expedition-items-header">
            <h3>Éléments à expédier</h3>
            <span class="expedition-items-count">{{ expeditionSuggestions.length }} type(s)</span>
          </div>
          <div class="expedition-items-table-wrap">
            <table class="expedition-items-table">
              <thead>
                <tr>
                  <th class="col-code">Code</th>
                  <th class="col-desig">Désignation</th>
                  <th class="col-num">Commandé</th>
                  <th class="col-num">Expédié</th>
                  <th class="col-num">Restant</th>
                  <th class="col-num">Stock</th>
                  <th class="col-qty">À expédier</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(sug, index) in expeditionSuggestions"
                  :key="sug.typeElementPrefabriqueId"
                  :class="{ 'row-alt': index % 2 === 1, 'row-zero-stock': sug.stockUsine === 0 }"
                >
                  <td class="col-code cell-mono">{{ sug.typeCode }}</td>
                  <td class="col-desig">{{ sug.typeLibelle }}</td>
                  <td class="col-num">{{ sug.quantiteCommandee }}</td>
                  <td class="col-num">{{ sug.quantiteDejaExpediee }}</td>
                  <td class="col-num" :class="{ 'cell-warn': sug.resteALivrer > sug.stockUsine }">{{ sug.resteALivrer }}</td>
                  <td class="col-num" :class="{ 'cell-danger': sug.stockUsine === 0 }">{{ sug.stockUsine }}</td>
                  <td class="col-qty">
                    <input
                      type="number"
                      min="0"
                      :max="sug.stockUsine"
                      :value="expeditionQuantites[sug.typeElementPrefabriqueId] ?? sug.quantiteSuggeree"
                      @input="setExpeditionQuantite(sug.typeElementPrefabriqueId, ($event.target as HTMLInputElement).value)"
                      class="qty-input"
                      :disabled="sug.stockUsine === 0"
                    />
                  </td>
                </tr>
              </tbody>
              <tfoot>
                <tr class="row-total">
                  <td colspan="2">TOTAL</td>
                  <td class="col-num">{{ expeditionSuggestions.reduce((s, r) => s + r.quantiteCommandee, 0) }}</td>
                  <td class="col-num">{{ expeditionSuggestions.reduce((s, r) => s + r.quantiteDejaExpediee, 0) }}</td>
                  <td class="col-num">{{ expeditionSuggestions.reduce((s, r) => s + r.resteALivrer, 0) }}</td>
                  <td class="col-num">{{ expeditionSuggestions.reduce((s, r) => s + r.stockUsine, 0) }}</td>
                  <td class="col-qty total-qty">{{ Object.values(expeditionQuantites).reduce((s, v) => s + (v > 0 ? v : 0), 0) }}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
        <p v-else-if="expeditionForm.siteId && !loadingSuggestions" class="empty-hint">
          Aucun élément à expédier pour ce site.
        </p>
        <div v-if="loadingSuggestions" class="loading-hint">Chargement des suggestions...</div>

        <div v-if="expeditionFormError" class="form-error">{{ expeditionFormError }}</div>
        <div class="form-actions">
          <BaseButton type="button" variant="ghost" @click="closeExpeditionModal">Annuler</BaseButton>
          <BaseButton type="submit" variant="primary" :loading="savingExpedition">Créer la livraison</BaseButton>
        </div>
      </form>
    </FormPage>
    <FormPage v-if="showLivraisonMateriauModal" :wide="true" @close="closeLivraisonMateriauModal">
      <template #title>Nouvelle réception matériau</template>
      <form class="modal-form" @submit.prevent="saveLivraisonMateriau">
        <div class="livraison-header-fields">
          <BaseSelect v-model="livraisonMateriauForm.projetId" label="Projet *" required>
            <option value="">-- Sélectionner un projet --</option>
            <option v-for="projet in projets" :key="projet.id" :value="String(projet.id)">
              {{ projet.nom }} ({{ projet.code }})
            </option>
          </BaseSelect>
          <BaseInput
            v-model="livraisonMateriauForm.dateLivraison"
            label="Date de réception *"
            type="date"
            required
          />
          <BaseInput
            v-model.trim="livraisonMateriauForm.nomFournisseur"
            label="Fournisseur"
            placeholder="Nom du fournisseur"
          />
        </div>

        <div v-if="loadingBesoinsModal" class="livraison-placeholder">Chargement des matériaux…</div>
        <div v-else-if="!livraisonMateriauForm.projetId" class="livraison-placeholder">
          Sélectionnez un projet pour afficher les matériaux prévus.
        </div>
        <div v-else-if="livraisonRows.length === 0" class="livraison-placeholder">
          Aucun plan d'approvisionnement pour ce projet. Définissez des besoins matériaux dans la fiche projet.
        </div>
        <div v-else class="livraison-table-wrap">
          <table class="livraison-table">
            <thead>
              <tr>
                <th>Matériau</th>
                <th>Unité</th>
                <th class="col-num">Qté prévue</th>
                <th class="col-num">Déjà reçue</th>
                <th class="col-num">Restant</th>
                <th class="col-qty">Qté à réceptionner</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(row, i) in livraisonRows" :key="row.typeMateriauId" :class="{ 'row-warning': Number(row.quantite) > (row.quantitePrevue - row.quantiteRecue) && (row.quantitePrevue - row.quantiteRecue) >= 0 }">
                <td class="cell-nom">{{ row.typeNom }}</td>
                <td class="cell-unite">{{ row.typeUnite }}</td>
                <td class="col-num">{{ row.quantitePrevue }}</td>
                <td class="col-num">{{ row.quantiteRecue }}</td>
                <td class="col-num" :class="{ 'restant-zero': row.quantitePrevue - row.quantiteRecue <= 0 }">
                  {{ (row.quantitePrevue - row.quantiteRecue).toFixed(1) }}
                </td>
                <td class="col-qty">
                  <input
                    v-model="livraisonRows[i].quantite"
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="0"
                    class="qty-input"
                  />
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div v-if="livraisonMateriauFormError" class="form-error">{{ livraisonMateriauFormError }}</div>
        <div class="form-actions">
          <BaseButton type="button" variant="ghost" @click="closeLivraisonMateriauModal">Annuler</BaseButton>
          <BaseButton type="submit" variant="primary" :loading="savingLivraisonMateriau" :disabled="livraisonRows.length === 0">
            Enregistrer
          </BaseButton>
        </div>
      </form>
    </FormPage>
  </AppLayout>
</template>

<script setup lang="ts">
import { computed, onMounted, reactive, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import AppLayout from '../components/AppLayout.vue';
import BaseButton from '../components/BaseButton.vue';
import BaseCard from '../components/BaseCard.vue';
import BaseInput from '../components/BaseInput.vue';
import BaseSelect from '../components/BaseSelect.vue';
import BaseTable, { type TableColumn } from '../components/BaseTable.vue';
import FormPage from '../components/FormPage.vue';
import { apiFetch } from '../utils/api';

interface Projet {
  id: number;
  nom: string;
  code: string;
}

interface Site {
  id: number;
  nom: string;
  projetId: number;
}

interface LotProduction {
  id: number;
  projetId: number;
  projetNom?: string;
  projetCode?: string;
  siteId: number | null;
  siteNom?: string | null;
  typeElementPrefabriqueId: number;
  quantiteProduite: number;
  dateProduction: string | null;
}

interface TypeElementPrefabrique {
  id: number;
  code: string;
  libelle: string;
}

interface FluxElement {
  typeElementPrefabriqueId: number;
  type: string;
  designation: string;
  quantiteCommandee: number;
  quantiteProduite: number;
  quantiteEnMaturation: number;
  quantiteStockUsine: number;
  quantiteExpediee: number;
}

interface LivrableProjection {
  typeElementPrefabriqueId: number;
  code: string;
  libelle: string;
  quantiteStockUsine: number;
  quantiteMaturationDisponible: number;
  quantiteLivrable: number;
}

interface TypeMateriau {
  id: number;
  nom: string;
  unite: string;
}

interface LivraisonMateriau {
  id: number;
  typeMateriauId: number;
  quantite: number;
  nomFournisseur: string | null;
  dateLivraison: string;
  projetId: number | null;
}

interface ExpeditionItemDraft {
  uid: number;
  typeElementPrefabriqueId: string;
  quantiteEnvoyee: string;
}

interface FluxRow extends FluxElement {
  resteAProduire: number;
  quantiteLivrableDate: number;
}

const router = useRouter();

const projets = ref<Projet[]>([]);
const sites = ref<Site[]>([]);
const typesElements = ref<TypeElementPrefabrique[]>([]);
const lots = ref<LotProduction[]>([]);
const fluxElements = ref<FluxElement[]>([]);
const livrablesProjection = ref<LivrableProjection[]>([]);

const activeTab = ref('flux');

const selectedProjetId = ref('');
const selectedSiteId = ref('');
const horizonDate = ref(toDateInput(addDays(new Date(), 1)));

const loadingLots = ref(false);
const loadingFlux = ref(false);
const error = ref<string | null>(null);

const showProductionModal = ref(false);
const savingProduction = ref(false);
const productionFormError = ref<string | null>(null);
const editingLot = ref<LotProduction | null>(null);

const showExpeditionModal = ref(false);
const savingExpedition = ref(false);
const expeditionFormError = ref<string | null>(null);

const typesMateriaux = ref<TypeMateriau[]>([]);
const livraisonsMateriaux = ref<LivraisonMateriau[]>([]);
const loadingLivraisonsMateriaux = ref(false);
const showLivraisonMateriauModal = ref(false);
const savingLivraisonMateriau = ref(false);
const livraisonMateriauFormError = ref<string | null>(null);

// ── Consommation matériaux usine ──
interface ConsommationUsine {
  id: number;
  dateConso: string;
  projet: string;
  numeroOp: string | null;
  numeroDa: string | null;
  uniteProd: string | null;
  designation: string;
  magasin: string | null;
  materiau: string;
  quantite: number;
}
const consommations = ref<ConsommationUsine[]>([]);
const loadingConso = ref(false);
const showConsoModal = ref(false);
const savingConso = ref(false);
const consoFormError = ref<string | null>(null);

interface ConsoRow {
  dateConso: string;
  projet: string;
  numeroOp: string;
  numeroDa: string;
  uniteProd: string;
  designation: string;
  magasin: string;
  materiau: string;
  quantite: string;
}

const consoRows = ref<ConsoRow[]>([
  { dateConso: toDateInput(new Date()), projet: '', numeroOp: '', numeroDa: '', uniteProd: '', designation: '', magasin: '', materiau: '', quantite: '' }
]);

const consoColumns = [
  { key: 'dateConso', label: 'Date' },
  { key: 'projet', label: 'Projet' },
  { key: 'numeroOp', label: 'N° OP' },
  { key: 'numeroDa', label: 'N° DA' },
  { key: 'uniteProd', label: 'Unité prod.' },
  { key: 'designation', label: 'Désignation' },
  { key: 'magasin', label: 'Magasin' },
  { key: 'materiau', label: 'Matériaux / Matériels' },
  { key: 'quantite', label: 'Quantité' }
];

// Besoins matériaux par projet (plan d'approvisionnement)
interface BesoinMateriau {
  id: number;
  typeMateriauId: number;
  quantitePrevue: number;
  quantiteRecue: number;
  typeNom: string | null;
  typeUnite: string | null;
}
const besoinsProjet = ref<BesoinMateriau[]>([]);

const productionForm = reactive({
  projetId: '',
  siteId: '',
  typeElementPrefabriqueId: '',
  quantiteProduite: '',
  dureeMaturationJours: '0',
  dateProduction: toDateInput(new Date())
});

interface ProductionRow {
  projetId: string;
  siteId: string;
  typeElementPrefabriqueId: string;
  quantiteProduite: string;
  dureeMaturationJours: string;
  dateProduction: string;
}

const productionRows = ref<ProductionRow[]>([
  { projetId: '', siteId: '', typeElementPrefabriqueId: '', quantiteProduite: '', dureeMaturationJours: '0', dateProduction: toDateInput(new Date()) }
]);

// Métriques production par type (commandé vs produit)
interface ProductionMetrique { typeElementPrefabriqueId: number; quantitePlanifiee: number; quantiteProduite: number; }
const productionMetriques = ref<ProductionMetrique[]>([]);

const productionRestantInfo = computed(() => {
  const typeId = toNumberOrNull(productionForm.typeElementPrefabriqueId);
  if (!typeId) return null;
  const m = productionMetriques.value.find(x => x.typeElementPrefabriqueId === typeId);
  if (!m) return null;
  return { commandee: m.quantitePlanifiee, produite: m.quantiteProduite, restant: m.quantitePlanifiee - m.quantiteProduite };
});

const productionDepasseCommandee = computed(() => {
  if (!productionRestantInfo.value) return false;
  const qty = toNumberOrNull(productionForm.quantiteProduite);
  if (!qty || qty <= 0) return false;
  return productionRestantInfo.value.produite + qty > productionRestantInfo.value.commandee;
});

const expeditionForm = reactive({
  projetId: '',
  siteId: '',
  dateExpedition: toDateInput(new Date()),
  idVehicule: '',
  nomChauffeur: '',
  carburantLitres: null as number | null,
  distanceKm: null as number | null
});

interface SuggestionItem {
  typeElementPrefabriqueId: number;
  typeCode: string;
  typeLibelle: string;
  quantiteCommandee: number;
  quantiteDejaExpediee: number;
  resteALivrer: number;
  stockUsine: number;
  quantiteSuggeree: number;
}
const expeditionSuggestions = ref<SuggestionItem[]>([]);
const expeditionQuantites = ref<Record<number, number>>({});
const loadingSuggestions = ref(false);

const livraisonMateriauForm = reactive({
  projetId: '',
  nomFournisseur: '',
  dateLivraison: toDateInput(new Date())
});

interface LivraisonRow {
  typeMateriauId: number;
  typeNom: string;
  typeUnite: string;
  quantitePrevue: number;
  quantiteRecue: number;
  quantite: string;
}
const livraisonRows = ref<LivraisonRow[]>([]);
const loadingBesoinsModal = ref(false);

const expeditionItems = ref<ExpeditionItemDraft[]>([
  { uid: 1, typeElementPrefabriqueId: '', quantiteEnvoyee: '' }
]);
const nextExpeditionItemUid = ref(2);

const fluxColumns: TableColumn[] = [
  { key: 'type', label: 'Type' },
  { key: 'designation', label: 'Désignation' },
  { key: 'quantiteCommandee', label: 'Commandée' },
  { key: 'quantiteProduite', label: 'Produite' },
  { key: 'resteAProduire', label: 'Reste' },
  { key: 'quantiteEnMaturation', label: 'Maturation' },
  { key: 'quantiteLivrableDate', label: 'Livrable date' },
  { key: 'quantiteStockUsine', label: 'Stock usine' },
  { key: 'quantiteExpediee', label: 'Expédiée' }
];

const lotColumns: TableColumn[] = [
  { key: 'dateProduction', label: 'Date' },
  { key: 'projet', label: 'Projet' },
  { key: 'site', label: 'Site' },
  { key: 'typeElement', label: 'Type' },
  { key: 'quantiteProduite', label: 'Qté produite' },
  { key: 'statutMaturation', label: 'Maturation' }
];

const livraisonMateriauColumns: TableColumn[] = [
  { key: 'dateLivraison', label: 'Date' },
  { key: 'typeMateriau', label: 'Matériau' },
  { key: 'quantite', label: 'Quantité' },
  { key: 'unite', label: 'Unité' },
  { key: 'projet', label: 'Projet' },
  { key: 'nomFournisseur', label: 'Fournisseur' }
];

// ── Expéditions / Livraisons ──
interface Expedition {
  id: number;
  projetId: number;
  siteId: number;
  siteNom?: string;
  dateExpedition: string;
  statut: string;
  idVehicule?: string;
  nomChauffeur?: string;
}
const expeditions = ref<Expedition[]>([]);
const loadingExpeditions = ref(false);
const expeditionStatusLoadingId = ref<number | null>(null);

const expeditionColumns: TableColumn[] = [
  { key: 'dateExpedition', label: 'Date' },
  { key: 'projet', label: 'Projet' },
  { key: 'site', label: 'Site' },
  { key: 'statut', label: 'Statut' },
  { key: 'idVehicule', label: 'Véhicule' },
  { key: 'nomChauffeur', label: 'Chauffeur' }
];

const expeditionsFiltered = computed(() => {
  const projetId = toNumberOrNull(selectedProjetId.value);
  const siteId = toNumberOrNull(selectedSiteId.value);
  return expeditions.value.filter(e => {
    if (projetId !== null && Number(e.projetId) !== projetId) return false;
    if (siteId !== null && Number(e.siteId) !== siteId) return false;
    return true;
  });
});

function getExpeditionStatutVariant(statut: string): 'default' | 'primary' | 'success' | 'warning' | 'error' {
  switch (statut) {
    case 'LIVRE': return 'success';
    case 'EN_TRANSIT': return 'primary';
    case 'PLANIFIE': return 'warning';
    case 'ANNULE': return 'error';
    default: return 'default';
  }
}

function buildLivraisonRows(besoins: BesoinMateriau[]): LivraisonRow[] {
  return besoins.map(b => {
    const typeMateriauId = Number(b.typeMateriauId);
    return {
      typeMateriauId,
      typeNom: b.typeNom ?? resolveTypeMateriauName(typeMateriauId),
      typeUnite: b.typeUnite ?? resolveTypeMateriauUnite(typeMateriauId),
      quantitePrevue: Number(b.quantitePrevue),
      quantiteRecue: Number(b.quantiteRecue),
      quantite: ''
    };
  });
}

const filteredSites = computed(() => {
  const projetId = toNumberOrNull(selectedProjetId.value);
  const scoped = projetId === null ? sites.value : sites.value.filter((site) => site.projetId === projetId);
  return [...scoped].sort((a, b) => a.nom.localeCompare(b.nom, 'fr'));
});

const productionFormSites = computed(() => {
  const projetId = toNumberOrNull(productionForm.projetId);
  if (projetId === null) {
    return [] as Site[];
  }
  return sites.value.filter((site) => site.projetId === projetId);
});

const expeditionFormSites = computed(() => {
  const projetId = toNumberOrNull(expeditionForm.projetId);
  if (projetId === null) {
    return [] as Site[];
  }
  return sites.value.filter((site) => site.projetId === projetId);
});

const livrableByType = computed(() => {
  return new Map(
    livrablesProjection.value.map((item) => [item.typeElementPrefabriqueId, item.quantiteLivrable] as const)
  );
});

const fluxRows = computed<FluxRow[]>(() => {
  return fluxElements.value.map((row) => ({
    ...row,
    resteAProduire: Math.max(row.quantiteCommandee - row.quantiteProduite, 0),
    quantiteLivrableDate: livrableByType.value.get(row.typeElementPrefabriqueId) ?? row.quantiteStockUsine
  }));
});

const fluxRowsFiltered = computed(() => fluxRows.value);

const lotsFiltered = computed(() => {
  const projetId = toNumberOrNull(selectedProjetId.value);
  const siteId = toNumberOrNull(selectedSiteId.value);

  return lots.value.filter((lot) => {
    if (projetId !== null && Number(lot.projetId) !== projetId) {
      return false;
    }
    if (siteId !== null && Number(lot.siteId) !== siteId) {
      return false;
    }
    return true;
  });
});

// ── Pagination ────────────────────────────────────────────────────────────────
const PAGE_SIZE = 10;

const pageReceptions  = ref(1);
const pageProduction  = ref(1);
const pageLivraisons  = ref(1);
const pageConso       = ref(1);

watch([selectedProjetId, selectedSiteId], () => {
  pageReceptions.value = 1;
  pageProduction.value = 1;
  pageLivraisons.value = 1;
  pageConso.value      = 1;
});


const receptionsSorted     = computed(() => [...livraisonsMateriaux.value].sort((a, b) => b.id - a.id));
const receptionsPaged      = computed(() => receptionsSorted.value.slice((pageReceptions.value - 1) * PAGE_SIZE, pageReceptions.value * PAGE_SIZE));
const receptionsTotalPages = computed(() => Math.max(1, Math.ceil(receptionsSorted.value.length / PAGE_SIZE)));

const productionSorted     = computed(() => [...lotsFiltered.value].sort((a, b) => b.id - a.id));
const productionPaged      = computed(() => productionSorted.value.slice((pageProduction.value - 1) * PAGE_SIZE, pageProduction.value * PAGE_SIZE));
const productionTotalPages = computed(() => Math.max(1, Math.ceil(productionSorted.value.length / PAGE_SIZE)));

const livraisonsSorted     = computed(() => [...expeditionsFiltered.value].sort((a, b) => b.id - a.id));
const livraisonsPaged      = computed(() => livraisonsSorted.value.slice((pageLivraisons.value - 1) * PAGE_SIZE, pageLivraisons.value * PAGE_SIZE));
const livraisonsTotalPages = computed(() => Math.max(1, Math.ceil(livraisonsSorted.value.length / PAGE_SIZE)));

const consoSorted     = computed(() => [...consommations.value].sort((a, b) => b.id - a.id));
const consoPaged      = computed(() => consoSorted.value.slice((pageConso.value - 1) * PAGE_SIZE, pageConso.value * PAGE_SIZE));
const consoTotalPages = computed(() => Math.max(1, Math.ceil(consoSorted.value.length / PAGE_SIZE)));
// ──────────────────────────────────────────────────────────────────────────────

const kpis = computed(() => {
  return fluxRowsFiltered.value.reduce(
    (acc, row) => ({
      resteAProduire: acc.resteAProduire + row.resteAProduire,
      enMaturation: acc.enMaturation + row.quantiteEnMaturation,
      livrableDate: acc.livrableDate + row.quantiteLivrableDate,
      stockUsine: acc.stockUsine + row.quantiteStockUsine
    }),
    {
      resteAProduire: 0,
      enMaturation: 0,
      livrableDate: 0,
      stockUsine: 0
    }
  );
});

watch(
  () => selectedProjetId.value,
  async () => {
    selectedSiteId.value = '';
    const projetId = toNumberOrNull(selectedProjetId.value);
    if (projetId !== null) {
      await loadSitesForProjet(projetId);
      // Auto-sélection si un seul site
      const projetSites = sites.value.filter(s => s.projetId === projetId);
      if (projetSites.length === 1) {
        selectedSiteId.value = String(projetSites[0].id);
      }
    }
  }
);

watch(
  () => [selectedProjetId.value, selectedSiteId.value, horizonDate.value],
  () => {
    loadFluxData();
    loadLivraisonsMateriaux();
  }
);

watch(
  () => productionForm.projetId,
  async () => {
    productionForm.siteId = '';
    productionMetriques.value = [];
    const projetId = toNumberOrNull(productionForm.projetId);
    if (projetId !== null) {
      await loadSitesForProjet(projetId);
      // Charger les métriques commandé/produit par type pour ce projet
      try {
        const res = await apiFetch(`/metriques/projets/${projetId}/production`);
        const items = (res as { items: ProductionMetrique[] }).items || [];
        productionMetriques.value = items;
      } catch { productionMetriques.value = []; }
    }
  }
);

watch(
  () => expeditionForm.projetId,
  async () => {
    expeditionForm.siteId = '';
    expeditionSuggestions.value = [];
    expeditionQuantites.value = {};
    const projetId = toNumberOrNull(expeditionForm.projetId);
    if (projetId !== null) {
      await loadSitesForProjet(projetId);
    }
  }
);

watch(
  () => expeditionForm.siteId,
  async () => {
    expeditionSuggestions.value = [];
    expeditionQuantites.value = {};
    const projetId = toNumberOrNull(expeditionForm.projetId);
    const siteId = toNumberOrNull(expeditionForm.siteId);
    if (projetId !== null && siteId !== null) {
      loadingSuggestions.value = true;
      try {
        const res = await apiFetch(`/expeditions/suggestion?projetId=${projetId}&siteId=${siteId}`);
        const items = (res as { items: SuggestionItem[] }).items || [];
        expeditionSuggestions.value = items;
        // Pré-remplir avec la quantité suggérée
        const qtMap: Record<number, number> = {};
        for (const item of items) {
          if (item.quantiteSuggeree > 0) qtMap[item.typeElementPrefabriqueId] = item.quantiteSuggeree;
        }
        expeditionQuantites.value = qtMap;
      } catch {
        expeditionSuggestions.value = [];
      } finally {
        loadingSuggestions.value = false;
      }
    }
  }
);

// Charger les besoins matériaux quand le projet change dans le formulaire livraison
watch(
  () => livraisonMateriauForm.projetId,
  async (newVal) => {
    livraisonRows.value = [];
    const projetId = toNumberOrNull(newVal);
    if (projetId !== null) {
      loadingBesoinsModal.value = true;
      try {
        await loadBesoinsProjet(projetId);
        livraisonRows.value = buildLivraisonRows(besoinsProjet.value);
      } finally {
        loadingBesoinsModal.value = false;
      }
    } else {
      besoinsProjet.value = [];
    }
  }
);

onMounted(async () => {
  await loadProjets();
  // Auto-sélection si un seul projet
  if (projets.value.length === 1 && !selectedProjetId.value) {
    selectedProjetId.value = String(projets.value[0].id);
  }
  await Promise.all([loadTypesElements(), loadLots(), loadTypesMateriaux()]);
  await Promise.all([loadFluxData(), loadLivraisonsMateriaux(), loadExpeditions(), loadConsommations()]);
});

async function loadProjets() {
  try {
    const response = await apiFetch('/projets');
    projets.value = (response as { items: Projet[] }).items || [];
  } catch (err) {
    error.value = err instanceof Error ? err.message : 'Erreur lors du chargement des projets';
  }
}

async function loadSitesForProjet(projetId: number) {
  try {
    const res = await apiFetch(`/projets/${projetId}/sites`);
    const items = (res as { items: Array<{ id: number; nom: string }> }).items || [];
    const newSites = items.map((site) => ({
      id: site.id,
      nom: site.nom,
      projetId
    })) satisfies Site[];
    sites.value = [
      ...sites.value.filter((s) => s.projetId !== projetId),
      ...newSites
    ];
  } catch (err) {
    console.error('Erreur lors du chargement des sites:', err);
  }
}

async function loadTypesElements() {
  try {
    const response = await apiFetch('/types-elements-prefabriques');
    typesElements.value = (response as { items: TypeElementPrefabrique[] }).items || [];
  } catch (err) {
    console.error('Erreur lors du chargement des types:', err);
  }
}

async function loadLots() {
  loadingLots.value = true;
  try {
    const response = await apiFetch('/production/lots-production?limit=500&offset=0');
    lots.value = (response as { items: LotProduction[] }).items || [];
  } catch (err) {
    error.value = err instanceof Error ? err.message : 'Erreur lors du chargement des lots';
  } finally {
    loadingLots.value = false;
  }
}

async function loadFluxData() {
  const projetId = toNumberOrNull(selectedProjetId.value);
  const siteId = toNumberOrNull(selectedSiteId.value);
  const projetIds = projetId !== null ? [projetId] : projets.value.map((p) => p.id);

  if (projetIds.length === 0) {
    fluxElements.value = [];
    livrablesProjection.value = [];
    return;
  }

  loadingFlux.value = true;
  try {
    const fluxParams = new URLSearchParams();
    if (siteId !== null) fluxParams.set('siteId', String(siteId));
    const fluxQs = fluxParams.toString() ? `?${fluxParams.toString()}` : '';

    const fluxResults = await Promise.all(
      projetIds.map((pid) =>
        apiFetch(`/metriques/projets/${pid}/flux-elements${fluxQs}`).catch(() => ({ items: [] }))
      )
    );

    const mergedMap = new Map<number, FluxElement>();
    fluxResults.forEach((res) => {
      ((res as { items: FluxElement[] }).items || []).forEach((item) => {
        const existing = mergedMap.get(item.typeElementPrefabriqueId);
        if (existing) {
          existing.quantiteCommandee += item.quantiteCommandee;
          existing.quantiteProduite += item.quantiteProduite;
          existing.quantiteEnMaturation += item.quantiteEnMaturation;
          existing.quantiteStockUsine += item.quantiteStockUsine;
          existing.quantiteExpediee += item.quantiteExpediee;
        } else {
          mergedMap.set(item.typeElementPrefabriqueId, { ...item });
        }
      });
    });
    fluxElements.value = Array.from(mergedMap.values());

    if (projetId !== null) {
      const livrableParams = new URLSearchParams({ dateCible: horizonDate.value });
      if (siteId !== null) livrableParams.set('siteId', String(siteId));
      const livrableRes = await apiFetch(`/projets/${projetId}/livrables?${livrableParams.toString()}`).catch(() => ({ items: [] }));
      livrablesProjection.value = (livrableRes as { items: LivrableProjection[] }).items || [];
    } else {
      livrablesProjection.value = [];
    }
  } catch (err) {
    error.value = err instanceof Error ? err.message : 'Erreur lors du chargement du flux usine';
    fluxElements.value = [];
    livrablesProjection.value = [];
  } finally {
    loadingFlux.value = false;
  }
}

async function loadTypesMateriaux() {
  try {
    const response = await apiFetch('/materiaux');
    typesMateriaux.value = (response as { items: TypeMateriau[] }).items || [];
  } catch {
    // non-bloquant
  }
}

async function loadLivraisonsMateriaux() {
  loadingLivraisonsMateriaux.value = true;
  try {
    const projetId = toNumberOrNull(selectedProjetId.value);
    const queryParams = new URLSearchParams();
    if (projetId !== null) {
      queryParams.set('projetId', String(projetId));
    }
    const qs = queryParams.toString();
    const response = await apiFetch(`/materiaux/livraisons${qs ? `?${qs}` : ''}`);
    livraisonsMateriaux.value = (response as { items: LivraisonMateriau[] }).items || [];
  } catch {
    livraisonsMateriaux.value = [];
  } finally {
    loadingLivraisonsMateriaux.value = false;
  }
}

function resolveTypeMateriauName(typeId: number): string {
  const found = typesMateriaux.value.find((t) => t.id === typeId);
  return found?.nom ?? `Type #${typeId}`;
}

function resolveTypeMateriauUnite(typeId: number): string {
  const found = typesMateriaux.value.find((t) => t.id === typeId);
  return found?.unite ?? '';
}

function resolveProjetLabel(projetId: number | null): string {
  if (!projetId) return '-';
  const found = projets.value.find((p) => p.id === projetId);
  return found ? `${found.nom} (${found.code})` : '-';
}

// ── Forcer fin maturation ──
const forcingMaturationId = ref<number | null>(null);

async function forcerFinMaturation(lotId: number) {
  forcingMaturationId.value = lotId;
  try {
    await apiFetch(`/production/lots-production/${lotId}/forcer-fin-maturation`, { method: 'POST' });
    await Promise.all([loadLots(), loadFluxData()]);
  } catch (e) {
    console.error('Erreur fin maturation:', e);
  } finally {
    forcingMaturationId.value = null;
  }
}

// ── Expéditions / Livraisons ──

async function loadExpeditions() {
  loadingExpeditions.value = true;
  try {
    const res = await apiFetch('/expeditions');
    expeditions.value = (res as { items: Expedition[] }).items || [];
  } catch {
    expeditions.value = [];
  } finally {
    loadingExpeditions.value = false;
  }
}

function viewExpedition(expedition: Expedition) {
  router.push({ name: 'expedition-detail', params: { id: expedition.id } });
}

async function updateExpeditionStatus(expeditionId: number, statut: string) {
  expeditionStatusLoadingId.value = expeditionId;
  try {
    await apiFetch(`/expeditions/${expeditionId}`, {
      method: 'PATCH',
      body: { statut }
    });
    await loadExpeditions();
  } catch (e) {
    console.error('Erreur changement statut:', e);
  } finally {
    expeditionStatusLoadingId.value = null;
  }
}

async function loadBesoinsProjet(projetId: number) {
  try {
    const res = await apiFetch(`/materiaux/besoins/${projetId}`);
    besoinsProjet.value = (res as { items: BesoinMateriau[] }).items || [];
  } catch {
    besoinsProjet.value = [];
  }
}

async function openLivraisonMateriauModal() {
  livraisonMateriauForm.projetId = selectedProjetId.value;
  livraisonMateriauForm.nomFournisseur = '';
  livraisonMateriauForm.dateLivraison = toDateInput(new Date());
  livraisonMateriauFormError.value = null;
  livraisonRows.value = [];
  showLivraisonMateriauModal.value = true;
  const projetId = toNumberOrNull(selectedProjetId.value);
  if (projetId !== null) {
    loadingBesoinsModal.value = true;
    try {
      await loadBesoinsProjet(projetId);
      livraisonRows.value = buildLivraisonRows(besoinsProjet.value);
    } finally {
      loadingBesoinsModal.value = false;
    }
  } else {
    besoinsProjet.value = [];
  }
}

function closeLivraisonMateriauModal() {
  showLivraisonMateriauModal.value = false;
  livraisonMateriauFormError.value = null;
}

async function saveLivraisonMateriau() {
  const projetId = toNumberOrNull(livraisonMateriauForm.projetId);
  if (projetId === null) {
    livraisonMateriauFormError.value = 'Le projet est obligatoire.';
    return;
  }
  if (!livraisonMateriauForm.dateLivraison) {
    livraisonMateriauFormError.value = 'La date de réception est requise.';
    return;
  }

  const rowsToSave = livraisonRows.value.filter(r => {
    const qty = Number(r.quantite);
    return qty > 0;
  });
  if (rowsToSave.length === 0) {
    livraisonMateriauFormError.value = 'Saisissez au moins une quantité.';
    return;
  }

  savingLivraisonMateriau.value = true;
  livraisonMateriauFormError.value = null;
  try {
    await Promise.all(rowsToSave.map(row =>
      apiFetch('/materiaux/livraisons', {
        method: 'POST',
        body: {
          typeMateriauId: Number(row.typeMateriauId),
          quantite: Number(row.quantite),
          nomFournisseur: livraisonMateriauForm.nomFournisseur || null,
          dateLivraison: livraisonMateriauForm.dateLivraison,
          projetId
        }
      })
    ));
    closeLivraisonMateriauModal();
    await loadLivraisonsMateriaux();
  } catch (err) {
    livraisonMateriauFormError.value = err instanceof Error ? err.message : 'Erreur lors de l\'enregistrement';
  } finally {
    savingLivraisonMateriau.value = false;
  }
}


async function loadConsommations() {
  loadingConso.value = true;
  try {
    const res = await apiFetch('/consommation-usine?limit=200');
    const items = (res as { items: Record<string, unknown>[] }).items || [];
    consommations.value = items.map((r) => ({
      id: Number(r['id']),
      dateConso: String(r['date_conso']).slice(0, 10),
      projet: String(r['projet']),
      numeroOp: r['numero_op'] ? String(r['numero_op']) : null,
      numeroDa: r['numero_da'] ? String(r['numero_da']) : null,
      uniteProd: r['unite_prod'] ? String(r['unite_prod']) : null,
      designation: String(r['designation']),
      magasin: r['magasin'] ? String(r['magasin']) : null,
      materiau: String(r['materiau']),
      quantite: Number(r['quantite'])
    }));
  } catch {
    consommations.value = [];
  } finally {
    loadingConso.value = false;
  }
}

function openConsoModal() {
  const projetNom = selectedProjetId.value
    ? (projets.value.find((p) => String(p.id) === selectedProjetId.value)?.nom ?? '')
    : '';
  consoRows.value = [{
    dateConso: toDateInput(new Date()),
    projet: projetNom,
    numeroOp: '', numeroDa: '', uniteProd: '',
    designation: '', magasin: '', materiau: '', quantite: ''
  }];
  consoFormError.value = null;
  showConsoModal.value = true;
}

function closeConsoModal() {
  showConsoModal.value = false;
  consoFormError.value = null;
}

function addConsoRow() {
  const last = consoRows.value[consoRows.value.length - 1];
  consoRows.value.push({
    dateConso: last?.dateConso ?? toDateInput(new Date()),
    projet: last?.projet ?? '',
    numeroOp: '', numeroDa: '', uniteProd: '',
    designation: '', magasin: '', materiau: '', quantite: ''
  });
}

function removeConsoRow(index: number) {
  if (consoRows.value.length > 1) consoRows.value.splice(index, 1);
}

async function saveConso() {
  for (const [i, row] of consoRows.value.entries()) {
    if (!row.dateConso || !row.projet || !row.designation || !row.materiau || !row.quantite) {
      consoFormError.value = `Ligne ${i + 1} : veuillez renseigner les champs obligatoires.`;
      return;
    }
  }
  savingConso.value = true;
  consoFormError.value = null;
  try {
    for (const row of consoRows.value) {
      await apiFetch('/consommation-usine', {
        method: 'POST',
        body: {
          dateConso: row.dateConso,
          projet: row.projet,
          numeroOp: row.numeroOp || null,
          numeroDa: row.numeroDa || null,
          uniteProd: row.uniteProd || null,
          designation: row.designation,
          magasin: row.magasin || null,
          materiau: row.materiau,
          quantite: Number(row.quantite)
        }
      });
    }
    closeConsoModal();
    await loadConsommations();
  } catch (err) {
    consoFormError.value = err instanceof Error ? err.message : 'Erreur lors de l\'enregistrement';
  } finally {
    savingConso.value = false;
  }
}

async function deleteConso(id: number) {
  if (!confirm('Supprimer cette entrée ?')) return;
  try {
    await apiFetch(`/consommation-usine/${id}`, { method: 'DELETE' });
    await loadConsommations();
  } catch (err) {
    console.error('Erreur suppression consommation:', err);
  }
}

function getSitesForRow(projetId: string): Site[] {
  const id = toNumberOrNull(projetId);
  if (id === null) return [];
  return sites.value.filter((s) => s.projetId === id);
}

async function onRowProjetChange(index: number) {
  const row = productionRows.value[index];
  row.siteId = '';
  const projetId = toNumberOrNull(row.projetId);
  if (projetId !== null && !sites.value.some((s) => s.projetId === projetId)) {
    await loadSitesForProjet(projetId);
  }
}

function addProductionRow() {
  const last = productionRows.value[productionRows.value.length - 1];
  productionRows.value.push({
    projetId: last?.projetId ?? '',
    siteId: '',
    typeElementPrefabriqueId: '',
    quantiteProduite: '',
    dureeMaturationJours: '0',
    dateProduction: last?.dateProduction ?? toDateInput(new Date())
  });
}

function removeProductionRow(index: number) {
  if (productionRows.value.length > 1) {
    productionRows.value.splice(index, 1);
  }
}

function openProductionModal() {
  editingLot.value = null;
  productionRows.value = [{
    projetId: selectedProjetId.value,
    siteId: selectedSiteId.value,
    typeElementPrefabriqueId: '',
    quantiteProduite: '',
    dureeMaturationJours: '0',
    dateProduction: toDateInput(new Date())
  }];
  productionFormError.value = null;
  showProductionModal.value = true;
}

function openEditLotModal(lot: LotProduction) {
  editingLot.value = lot;
  productionForm.projetId = String(lot.projetId);
  productionForm.siteId = lot.siteId ? String(lot.siteId) : '';
  productionForm.typeElementPrefabriqueId = String(lot.typeElementPrefabriqueId);
  productionForm.quantiteProduite = String(lot.quantiteProduite);
  productionForm.dureeMaturationJours = '0';
  productionForm.dateProduction = lot.dateProduction ?? toDateInput(new Date());
  productionFormError.value = null;
  showProductionModal.value = true;
}

function closeProductionModal() {
  showProductionModal.value = false;
  editingLot.value = null;
  productionFormError.value = null;
}

async function saveProduction() {
  savingProduction.value = true;
  productionFormError.value = null;

  try {
    if (editingLot.value) {
      // Mode édition : formulaire simple
      const projetId = toNumberOrNull(productionForm.projetId);
      const typeElementPrefabriqueId = toNumberOrNull(productionForm.typeElementPrefabriqueId);
      const quantiteProduite = toNumberOrNull(productionForm.quantiteProduite);
      const dureeMaturationJours = toNumberOrNull(productionForm.dureeMaturationJours);
      const siteId = toNumberOrNull(productionForm.siteId);

      if (projetId === null || typeElementPrefabriqueId === null || quantiteProduite === null || dureeMaturationJours === null) {
        productionFormError.value = 'Veuillez renseigner les champs obligatoires.';
        return;
      }
      if (!productionForm.dateProduction) {
        productionFormError.value = 'La date de production est requise.';
        return;
      }

      await apiFetch(`/production/lots-production/${editingLot.value.id}`, {
        method: 'PATCH',
        body: { siteId, typeElementPrefabriqueId, quantiteProduite, dureeMaturationJours, dateProduction: productionForm.dateProduction }
      });
    } else {
      // Mode création : multi-lignes
      for (const [i, row] of productionRows.value.entries()) {
        if (!row.projetId || !row.typeElementPrefabriqueId || !row.quantiteProduite || !row.dateProduction) {
          productionFormError.value = `Ligne ${i + 1} : veuillez renseigner les champs obligatoires.`;
          return;
        }
      }

      for (const row of productionRows.value) {
        await apiFetch(`/projets/${row.projetId}/lots-production`, {
          method: 'POST',
          body: {
            siteId: toNumberOrNull(row.siteId),
            typeElementPrefabriqueId: Number(row.typeElementPrefabriqueId),
            quantiteProduite: Number(row.quantiteProduite),
            dureeMaturationJours: Number(row.dureeMaturationJours),
            dateProduction: row.dateProduction
          }
        });
      }
    }

    closeProductionModal();
    await Promise.all([loadLots(), loadFluxData()]);
  } catch (err) {
    productionFormError.value = err instanceof Error ? err.message : 'Erreur lors de l\'enregistrement';
  } finally {
    savingProduction.value = false;
  }
}

function openExpeditionModal() {
  expeditionForm.projetId = selectedProjetId.value;
  expeditionForm.siteId = selectedSiteId.value;
  expeditionForm.dateExpedition = toDateInput(new Date());
  expeditionForm.idVehicule = '';
  expeditionForm.nomChauffeur = '';
  expeditionForm.carburantLitres = null;
  expeditionForm.distanceKm = null;
  expeditionSuggestions.value = [];
  expeditionQuantites.value = {};
  expeditionFormError.value = null;
  showExpeditionModal.value = true;
}

function closeExpeditionModal() {
  showExpeditionModal.value = false;
  expeditionFormError.value = null;
  expeditionSuggestions.value = [];
  expeditionQuantites.value = {};
}

function setExpeditionQuantite(typeId: number, value: string) {
  const qty = Number(value);
  if (Number.isFinite(qty) && qty >= 0) {
    expeditionQuantites.value[typeId] = qty;
  } else {
    delete expeditionQuantites.value[typeId];
  }
}

async function saveExpedition() {
  const projetId = toNumberOrNull(expeditionForm.projetId);
  const siteId = toNumberOrNull(expeditionForm.siteId);
  if (projetId === null || siteId === null) {
    expeditionFormError.value = 'Projet et site sont obligatoires.';
    return;
  }
  if (!expeditionForm.dateExpedition) {
    expeditionFormError.value = 'La date d\'expédition est requise.';
    return;
  }

  // Collecter les items avec quantité > 0
  const items = Object.entries(expeditionQuantites.value)
    .map(([typeId, qty]) => ({ typeElementPrefabriqueId: Number(typeId), quantiteEnvoyee: qty }))
    .filter(item => item.quantiteEnvoyee > 0);

  if (items.length === 0) {
    expeditionFormError.value = 'Veuillez renseigner au moins une quantité à expédier.';
    return;
  }

  savingExpedition.value = true;
  expeditionFormError.value = null;
  try {
    const payload: Record<string, unknown> = {
      siteId,
      dateExpedition: expeditionForm.dateExpedition,
      items
    };

    if (expeditionForm.idVehicule) payload.idVehicule = expeditionForm.idVehicule;
    if (expeditionForm.nomChauffeur) payload.nomChauffeur = expeditionForm.nomChauffeur;
    const carb = Number(expeditionForm.carburantLitres);
    const dist = Number(expeditionForm.distanceKm);
    if (Number.isFinite(carb) && carb > 0) payload.carburantLitres = carb;
    if (Number.isFinite(dist) && dist > 0) payload.distanceKm = dist;

    await apiFetch(`/projets/${projetId}/expeditions-avec-items`, {
      method: 'POST',
      body: payload
    });

    closeExpeditionModal();
    await Promise.all([loadFluxData(), loadExpeditions()]);
  } catch (err) {
    expeditionFormError.value = err instanceof Error ? err.message : 'Erreur lors de la création de la livraison';
  } finally {
    savingExpedition.value = false;
  }
}

function resolveTypeLabel(typeId: number): string {
  const found = typesElements.value.find((type) => type.id === typeId);
  return found ? `${found.libelle} (${found.code})` : `Type #${typeId}`;
}

function viewLot(lot: LotProduction) {
  router.push({ name: 'production-detail', params: { id: lot.id } });
}

function toNumberOrNull(value: string): number | null {
  if (value.trim() === '') {
    return null;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString('fr-FR');
}

function toDateInput(date: Date) {
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function addDays(base: Date, days: number) {
  const copy = new Date(base.getTime());
  copy.setDate(copy.getDate() + days);
  return copy;
}
</script>

<style scoped>
.usine-page {
  max-width: 1400px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: var(--spacing-lg);
}

.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--spacing-md);
}

.pagination-bar {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-sm);
  padding: var(--spacing-sm) var(--spacing-md);
  border-top: 1px solid var(--color-border-light);
  background: var(--color-bg-secondary);
  border-radius: 0 0 var(--radius-lg) var(--radius-lg);
}

.pag-btn {
  width: 32px;
  height: 32px;
  border: 1px solid var(--color-border-light);
  background: var(--color-bg-primary);
  border-radius: var(--radius-sm);
  cursor: pointer;
  font-size: var(--font-size-base);
  color: var(--color-text-primary);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s, border-color 0.15s;
}

.pag-btn:hover:not(:disabled) {
  background: var(--color-bg-secondary);
  border-color: var(--color-primary);
  color: var(--color-primary);
}

.pag-btn:disabled {
  opacity: 0.35;
  cursor: not-allowed;
}

.pag-info {
  font-size: var(--font-size-sm);
  color: var(--color-text-secondary);
  min-width: 60px;
  text-align: center;
}

.page-tabs {
  display: flex;
  gap: 2px;
  flex-wrap: wrap;
  align-items: center;
  border-bottom: 2px solid var(--color-border-light);
  margin-bottom: var(--spacing-lg);
}

.tab-actions {
  margin-left: auto;
  padding-bottom: 2px;
}

.page-tab {
  padding: var(--spacing-xs) var(--spacing-md);
  border: none;
  background: transparent;
  color: var(--color-text-secondary);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  cursor: pointer;
  border-bottom: 2px solid transparent;
  margin-bottom: -2px;
  border-radius: var(--radius-sm) var(--radius-sm) 0 0;
  transition: color 0.15s, border-color 0.15s;
  white-space: nowrap;
}

.page-tab:hover {
  color: var(--color-primary);
  background: var(--color-bg-secondary);
}

.page-tab.active {
  color: var(--color-primary);
  border-bottom-color: var(--color-primary);
  font-weight: var(--font-weight-semibold);
}

.page-header h1 {
  margin: 0;
}

.page-actions {
  display: flex;
  gap: var(--spacing-sm);
}

.filters-card {
  padding: var(--spacing-md);
}

.filters-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: var(--spacing-md);
}

.kpis-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(190px, 1fr));
  gap: var(--spacing-md);
}

.kpi-card {
  padding: var(--spacing-lg);
  text-align: center;
}

.kpi-value {
  font-size: var(--font-size-4xl);
  font-weight: var(--font-weight-bold);
  color: var(--color-primary);
}

.kpi-label {
  margin-top: var(--spacing-xs);
  color: var(--color-text-secondary);
  font-size: var(--font-size-sm);
}

.panel-section {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-sm);
}

.section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.section-header h2 {
  margin: 0;
}

.error {
  padding: var(--spacing-md);
  background: #fee;
  border: 1px solid #fcc;
  border-radius: var(--radius-md);
  color: #c33;
}

.modal-form {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-md);
}

.form-error {
  padding: var(--spacing-sm);
  border: 1px solid #fcc;
  border-radius: var(--radius-md);
  background: #fee;
  color: #c33;
  font-size: var(--font-size-sm);
}

.form-warning {
  padding: var(--spacing-sm);
  border: 1px solid #e8c860;
  border-radius: var(--radius-md);
  background: #fef9e7;
  color: #8a6d00;
  font-size: var(--font-size-sm);
}

.besoin-info {
  font-size: var(--font-size-sm);
  color: var(--color-text-secondary);
  padding: var(--spacing-xs) 0;
}

/* ── Tableau réception matériaux ── */
.livraison-header-fields {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: var(--spacing-md);
  margin-bottom: var(--spacing-md);
}

.livraison-placeholder {
  padding: var(--spacing-lg);
  text-align: center;
  color: var(--color-text-secondary);
  font-size: var(--font-size-sm);
  background: var(--color-bg);
  border-radius: var(--radius-md);
  border: 1px dashed var(--color-border);
}

.livraison-table-wrap {
  overflow-x: auto;
  border: 1px solid var(--color-border-light);
  border-radius: var(--radius-md);
  margin-bottom: var(--spacing-md);
}

.livraison-table {
  width: 100%;
  border-collapse: collapse;
  font-size: var(--font-size-sm);
}

.livraison-table th {
  background: var(--color-bg);
  padding: 8px 12px;
  text-align: left;
  font-size: var(--font-size-xs);
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--color-text-secondary);
  border-bottom: 1px solid var(--color-border-light);
}

.livraison-table td {
  padding: 8px 12px;
  border-bottom: 1px solid var(--color-border-light);
  vertical-align: middle;
}

.livraison-table tbody tr:last-child td {
  border-bottom: none;
}

.livraison-table .col-num {
  text-align: right;
  width: 110px;
}

.livraison-table .col-qty {
  width: 150px;
}

.livraison-table .cell-nom {
  font-weight: 500;
}

.livraison-table .cell-unite {
  color: var(--color-text-secondary);
  font-size: var(--font-size-xs);
  white-space: nowrap;
}

.livraison-table .restant-zero {
  color: var(--color-text-muted);
}

.livraison-table .row-warning {
  background: #fffbeb;
}

.qty-input {
  width: 100%;
  height: 34px;
  padding: 0 8px;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-sm);
  font-size: var(--font-size-sm);
  text-align: right;
  background: var(--color-surface);
  color: var(--color-text);
}

.qty-input:focus {
  outline: none;
  border-color: var(--color-primary);
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: var(--spacing-sm);
}

.expedition-form-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--spacing-md);
}

.expedition-items-section {
  margin-top: var(--spacing-lg);
  border: 1px solid var(--color-border-light);
  border-radius: var(--radius-md);
  overflow: hidden;
}

.expedition-items-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--spacing-sm) var(--spacing-md);
  background: var(--color-bg-secondary);
  border-bottom: 1px solid var(--color-border-light);
}

.expedition-items-header h3 {
  margin: 0;
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
}

.expedition-items-count {
  font-size: var(--font-size-xs);
  color: var(--color-text-secondary);
}

.expedition-items-table-wrap {
  overflow-x: auto;
}

.expedition-items-table {
  width: 100%;
  border-collapse: collapse;
  font-size: var(--font-size-sm);
}

.expedition-items-table th {
  padding: var(--spacing-xs) var(--spacing-sm);
  background: var(--color-bg-tertiary, #eef1f6);
  border-bottom: 1px solid var(--color-border-light);
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-semibold);
  text-transform: uppercase;
  letter-spacing: 0.3px;
  color: var(--color-text-secondary);
  white-space: nowrap;
}

.expedition-items-table td {
  padding: var(--spacing-xs) var(--spacing-sm);
  border-bottom: 1px solid var(--color-border-light);
}

.col-code { width: 100px; }
.col-desig { min-width: 140px; }
.col-num { text-align: right; width: 80px; font-variant-numeric: tabular-nums; }
.col-qty { width: 100px; text-align: center; }

.cell-mono { font-family: var(--font-mono, monospace); font-size: var(--font-size-xs); }
.cell-warn { color: #b45309; font-weight: var(--font-weight-semibold); }
.cell-danger { color: #c33; font-weight: var(--font-weight-semibold); }

.row-alt { background: var(--color-bg-secondary); }
.row-zero-stock { opacity: 0.5; }

.row-total {
  background: var(--color-bg-secondary);
  font-weight: var(--font-weight-semibold);
}

.row-total td {
  padding: var(--spacing-sm);
  border-top: 2px solid var(--color-border-light);
}

.total-qty {
  font-size: var(--font-size-base);
  color: var(--color-primary);
}

.qty-input {
  width: 80px;
  padding: var(--spacing-xs) var(--spacing-sm);
  border: 1px solid var(--color-border-medium);
  border-radius: var(--radius-md);
  font-size: var(--font-size-sm);
  text-align: right;
  font-variant-numeric: tabular-nums;
}

.qty-input:focus {
  outline: none;
  border-color: var(--color-primary);
  box-shadow: 0 0 0 2px rgba(30, 86, 217, 0.15);
}

.qty-input:disabled {
  background: var(--color-bg-tertiary, #eef1f6);
  cursor: not-allowed;
  opacity: 0.5;
}

.empty-hint { text-align: center; color: var(--color-text-secondary); padding: var(--spacing-lg); font-size: var(--font-size-sm); }
.loading-hint { text-align: center; color: var(--color-text-secondary); padding: var(--spacing-md); font-size: var(--font-size-sm); }

.items-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.items-header h3 {
  margin: 0;
  font-size: var(--font-size-lg);
}

.item-row {
  display: grid;
  grid-template-columns: 2fr 1fr auto;
  gap: var(--spacing-sm);
  align-items: end;
}

@media (max-width: 900px) {
  .item-row {
    grid-template-columns: 1fr;
  }
}

/* ── Formulaire production multi-lignes ── */

.production-rows-table-wrap {
  overflow-x: auto;
  border: 1px solid var(--color-border-light);
  border-radius: var(--radius-md);
}

.production-rows-table {
  width: 100%;
  border-collapse: collapse;
  font-size: var(--font-size-sm);
}

.production-rows-table th {
  padding: var(--spacing-xs) var(--spacing-sm);
  background: var(--color-bg-tertiary, #eef1f6);
  border-bottom: 1px solid var(--color-border-light);
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-semibold);
  text-transform: uppercase;
  letter-spacing: 0.3px;
  color: var(--color-text-secondary);
  white-space: nowrap;
  text-align: left;
}

.production-rows-table td {
  padding: var(--spacing-xs) var(--spacing-sm);
  border-bottom: 1px solid var(--color-border-light);
  vertical-align: middle;
}

.production-rows-table tbody tr:last-child td {
  border-bottom: none;
}

.row-select {
  width: 100%;
  min-width: 120px;
  padding: 4px 6px;
  border: 1px solid var(--color-border-medium);
  border-radius: var(--radius-sm);
  font-size: var(--font-size-sm);
  background: var(--color-bg-primary, #fff);
  color: var(--color-text-primary);
}

.row-input {
  width: 80px;
  padding: 4px 6px;
  border: 1px solid var(--color-border-medium);
  border-radius: var(--radius-sm);
  font-size: var(--font-size-sm);
  font-variant-numeric: tabular-nums;
}

.row-input-sm {
  width: 60px;
}

.row-delete-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  padding: 0;
  border: none;
  border-radius: var(--radius-sm);
  background: transparent;
  color: var(--color-text-secondary);
  font-size: 16px;
  cursor: pointer;
  transition: background 0.15s, color 0.15s;
}

.row-delete-btn:hover:not(:disabled) {
  background: #fee2e2;
  color: #c33;
}

.row-delete-btn:disabled {
  opacity: 0.3;
  cursor: not-allowed;
}

/* Mobile : cartes (cachées sur desktop) */
.production-rows-cards {
  display: none;
}

.production-row-card {
  border: 1px solid var(--color-border-light);
  border-radius: var(--radius-md);
  overflow: hidden;
}

.row-card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--spacing-xs) var(--spacing-sm);
  background: var(--color-bg-secondary);
  border-bottom: 1px solid var(--color-border-light);
}

.row-card-num {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text-secondary);
}

.row-card-fields {
  padding: var(--spacing-sm);
  display: flex;
  flex-direction: column;
  gap: var(--spacing-sm);
}

.row-card-field {
  display: flex;
  flex-direction: column;
  gap: 2px;
  font-size: var(--font-size-sm);
}

.row-card-field span {
  font-size: var(--font-size-xs);
  color: var(--color-text-secondary);
  font-weight: var(--font-weight-medium);
}

.row-card-field .row-select,
.row-card-field .row-input {
  width: 100%;
  min-width: unset;
}

@media (max-width: 640px) {
  .production-rows-table-wrap {
    display: none;
  }

  .production-rows-cards {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-sm);
  }
}
</style>
